import { Routes, Route, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import { MainLayout } from "@shared/components/layout";
import { PageTransition, useAnimation } from "@shared/features/transitions";
import { PageLoader, AuthErrorBoundary } from "@shared/components/feedback";
import { SuperAdminRoute, UnauthorizedPage } from "@shared/features/auth";

// Lazy load page components
const HomePage = lazy(() => import("./pages/HomePage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const TransparencyPage = lazy(() => import("./pages/TransparencyPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AuthRedirectPage = lazy(() => import("./pages/AuthRedirectPage"));
const NotFoundPage = lazy(() =>
  import("@shared/features/errors").then((module) => ({
    default: module.NotFoundPage,
  })),
);
const AnonymousExportVerificationPage = lazy(() =>
  import("@shared/features/privacy").then((m) => ({
    default: m.AnonymousExportVerificationPage,
  })),
);

// Admin pages
const LoginPage = lazy(() => import("./pages/admin/LoginPage"));
const SecurityPage = lazy(() => import("./pages/admin/SecurityPage"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const ClientsPage = lazy(() => import("./pages/admin/ClientsPage"));
const MonitoringPage = lazy(() => import("./pages/admin/MonitoringPage"));
const ApiDashboardPage = lazy(() => import("./pages/admin/ApiDashboardPage"));
const AdminPrivacyPage = lazy(() =>
  import("@shared/features/privacy").then((module) => ({
    default: module.AdminPrivacyPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@shared/pages/admin").then((m) => ({ default: m.SettingsPage })),
);

function AppRoutes() {
  const location = useLocation();
  const { defaultPageTransition } = useAnimation();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={location}>
        <Route element={<MainLayout />}>
          <Route
            path="/"
            element={
              <PageTransition transition={defaultPageTransition}>
                <HomePage />
              </PageTransition>
            }
          />
          <Route
            path="/contact"
            element={
              <PageTransition transition={defaultPageTransition}>
                <ContactPage />
              </PageTransition>
            }
          />
          <Route
            path="/product"
            element={
              <PageTransition transition={defaultPageTransition}>
                <ProductPage />
              </PageTransition>
            }
          />
          <Route
            path="/transparency"
            element={
              <PageTransition transition={defaultPageTransition}>
                <TransparencyPage />
              </PageTransition>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageTransition transition={defaultPageTransition}>
                <PrivacyPage />
              </PageTransition>
            }
          />
          <Route
            path="/terms"
            element={
              <PageTransition transition={defaultPageTransition}>
                <TermsPage />
              </PageTransition>
            }
          />
          <Route
            path="/privacy/verify-export/:token"
            element={
              <PageTransition transition={defaultPageTransition}>
                <AnonymousExportVerificationPage />
              </PageTransition>
            }
          />
          <Route
            path="/auth/redirect"
            element={
              <AuthErrorBoundary fallbackMessage="We're having trouble processing the authentication redirect. Please try again.">
                <PageTransition transition={defaultPageTransition}>
                  <AuthRedirectPage />
                </PageTransition>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/admin/login"
            element={
              <PageTransition transition={defaultPageTransition}>
                <LoginPage />
              </PageTransition>
            }
          />
          <Route
            path="/admin/unauthorized"
            element={
              <PageTransition transition={defaultPageTransition}>
                <UnauthorizedPage />
              </PageTransition>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <DashboardPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <ClientsPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/monitoring"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <MonitoringPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/security"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <SecurityPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/privacy"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <AdminPrivacyPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/api-dashboard"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <ApiDashboardPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <SettingsPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          {/* Admin default route - redirect to dashboard */}
          <Route
            path="/admin"
            element={
              <SuperAdminRoute>
                <PageTransition transition={defaultPageTransition}>
                  <DashboardPage />
                </PageTransition>
              </SuperAdminRoute>
            }
          />
          <Route
            path="*"
            element={
              <PageTransition transition={defaultPageTransition}>
                <NotFoundPage />
              </PageTransition>
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
